import { Icon } from 'hfs-common-ui';
import React from 'react';
import { css } from 'styled-components';
import FormattedMessageWrapper from '~/components/views/FormattedMessageWrapper/FormattedMessageWrapper';
import * as S from './styled';

type BaseType = 'error' | 'warning' | 'info';

const modalMessageIcons: Record<string, React.ReactNode> = {
  error: <Icon type="notification.error" size="medium" color="red500" />,
  error_outlined: <Icon type="notification.error_outlined" size="medium" color="red500" />,
  warning_outlined: <Icon type="notification.error_outlined" size="medium" color="yellow500" />,
  warning: <Icon type="notification.error" size="medium" color="yellow500" />,
  info: <Icon type="notification.info" size="medium" color="blue500" />,
  info_outlined: <Icon type="notification.info_outlined" size="medium" color="blue500" />,
};

interface I18nMessage {
  key: string;
  values?: Record<string, unknown>;
}

interface Props {
  messageId: string;
  type: BaseType;
  position: 'top' | 'bottom';
  icon?: React.ReactNode;
  useOutlined?: boolean; // when true, prefer the outlined variant
  title?: I18nMessage | null;
  message?: I18nMessage | string | null;
  id?: string;
  testId?: string;
  styles?: ReturnType<typeof css>;
}

const ModalMessageView: React.FC<Props> = ({
  messageId,
  type,
  position,
  icon,
  useOutlined = true,
  title = null,
  message = null,
  id,
  testId,
  styles,
}) => {
  const iconKey = useOutlined ? `${type}_outlined` : type;
  const messageIcon = icon ?? modalMessageIcons[iconKey] ?? modalMessageIcons[type];

  return (
    <S.ModalMessage
      key={messageId}
      //@ts-ignore styled prop
      $position={position}
      id={id ? `${id}-modal-message-box` : 'modal-message'}
      data-testid={testId ? `${testId}-modal-message-box` : 'modal-message'}
      $type={type}
      $customStyled={styles}
    >
      <S.MessageIcon
        id={id ? `${id}-message-icon` : 'message-icon'}
        data-testid={testId ? `${testId}-message-icon` : 'message-icon'}
      >
        {messageIcon}
      </S.MessageIcon>
      <S.MessageContent>
        {title && (
          <S.MessageTitle
            id={id ? `${id}-message-title` : 'message-title'}
            data-testid={testId ? `${testId}-message-title` : 'message-title'}
          >
            <FormattedMessageWrapper langKey={title.key} values={title.values} />
          </S.MessageTitle>
        )}
        {message && (
          <S.MessageText
            id={id ? `${id}-message-description` : 'message-description'}
            data-testid={testId ? `${testId}-message-description` : 'message-description'}
          >
            {typeof message === 'string' ? (
              message
            ) : (
              <FormattedMessageWrapper langKey={message.key} values={message.values} />
            )}
          </S.MessageText>
        )}
      </S.MessageContent>
    </S.ModalMessage>
  );
};

export default ModalMessageView;
